import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Send, Check } from 'lucide-react';
import { dbService, ContactMessage as ContactMessageType } from '../lib/supabase';
import { 
  validateEmail, 
  validateName, 
  validateTextArea,
} from '../utils/validations';
import Dompurify from 'dompurify';
import { SUPPORT_EMAIL } from '../constants/messages';

// Ensure all user input is sanitized to prevent XSS attacks
const sanitizeInput = (input: string) => {
  return Dompurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    reason: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fatalError, setFatalError] = useState<Error | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const reasons = [
    'General Inquiry',
    'Partnership Opportunity',
    'Press/Media',
    'Investor Relations',
    'Technical Support',
    'Other'
  ];

  if (fatalError) {
    throw fatalError;
  }

  // Real-time field validation
  const validateField = (name: string, value: string) => {
    let validation;

    switch (name) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'message':
        validation = validateTextArea(value, 'Message', 10, 2000, true);
        break;
      case 'reason':
        validation = { isValid: !!value.trim(), error: value.trim() ? undefined : 'Please select a reason for contact' };
        break;
      default:
        validation = { isValid: true };
    }

    setValidationErrors(prev => ({
      ...prev,
      [name]: validation.isValid ? '' : (validation.error || 'Invalid value')
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear submission error when user starts typing
    setSubmissionError(null);

    // Real-time validation
    validateField(name, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionError(null);
    setValidationErrors({});

    try {
      // Validate all fields before submission
      
      const validationErrors: string[] = [];

      const nameValidation = validateName(formData.name);
      if (!nameValidation.isValid) validationErrors.push(nameValidation.error!);

      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) validationErrors.push(emailValidation.error!);

      if (!formData.reason.trim()) {
        validationErrors.push('Please select a reason for contact');
      }

      const messageValidation = validateTextArea(formData.message, 'Message', 10, 2000, true);
      if (!messageValidation.isValid) validationErrors.push(messageValidation.error!);

      if (validationErrors.length > 0) {
        throw new Error(`Please fix the following errors: ${validationErrors.join(', ')}`);
      }

      const messageData: ContactMessageType = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        company: sanitizeInput(formData.company) || undefined,
        reason: sanitizeInput(formData.reason),
        message: sanitizeInput(formData.message)
      };

      await dbService.createContactMessage(messageData);
      setSubmitted(true);
    } catch (error: unknown | null) {
      console.error('Error submitting contact message:', error);

      const err = error as { message?: string };

      if (err?.message?.includes('Please fix the following errors')) {
        setSubmissionError(err.message.replace('Please fix the following errors: ', ''));
      } else {
        setSubmissionError('There was an error sending your message. Please try again.');
        setFatalError(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-white/50"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-16 h-16 bg-gradient-to-r from-lime-green to-soft-teal rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <Check className="w-8 h-8 text-white" />
          </motion.div>

          <h2 className="text-2xl font-bold text-charcoal mb-4">Message Sent!</h2>
          <p className="text-slate-gray mb-6">
            Thanks for reaching out! We'll get back to you within 24 hours.
          </p>

          <Link to="/">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
            >
              Return Home
            </motion.button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-electric-blue/20 rounded-full"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0.2, 0.6, 0.2]
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: 'linear'
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      {/* Header */}
      <div className="relative z-10 bg-white/95 backdrop-blur-sm border-b border-light-gray">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Link to="/" className="inline-flex items-center text-electric-blue hover:text-deep-indigo transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-charcoal mb-2">Get in Touch</h1>
            <p className="text-sm sm:text-base text-slate-gray">We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 sm:space-y-8"
          >
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-charcoal mb-4 sm:mb-6">Let's Connect</h2>
              <p className="text-base sm:text-lg text-slate-gray leading-relaxed">
                Whether you're a biotech startup looking for manufacturing partners,
                a CDMO interested in joining our network, or an investor exploring opportunities,
                we'd love to hear from you.
              </p>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-start space-x-3 sm:space-x-4 p-4 sm:p-5 bg-white/80 backdrop-blur-sm rounded-xl border border-white/50 shadow-lg"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-electric-blue to-soft-teal rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-charcoal mb-1">Email Us</h3>
                  <p className="text-sm sm:text-base text-slate-gray">{SUPPORT_EMAIL}</p>
                  <p className="text-sm text-slate-gray/70">We'll respond within 24 hours</p>
                </div>
              </motion.div>
            </div>

            <div className="bg-gradient-to-r from-electric-blue/10 to-soft-teal/10 border border-electric-blue/30 rounded-xl p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-deep-indigo mb-2">Partnership Inquiries</h3>
              <p className="text-slate-gray text-sm">
                Interested in partnering with us? We're always open to strategic partnerships 
                that can help advance pharmaceutical manufacturing.
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 border border-white/50">
              <h2 className="text-xl sm:text-2xl font-bold text-charcoal mb-4 sm:mb-6">Send us a Message</h2>

              <div className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <motion.div whileHover={{ scale: 1.02 }}>
                    <label htmlFor="name" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.name
                          ? 'border-red-300 focus:ring-red-500'
                          : 'border-slate-gray/30'
                        }`}
                      placeholder="Your full name"
                    />
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.02 }}>
                    <label htmlFor="email" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.email
                          ? 'border-red-300 focus:ring-red-500'
                          : 'border-slate-gray/30'
                        }`}
                      placeholder="your@email.com"
                    />
                  </motion.div>
                </div>

                <motion.div whileHover={{ scale: 1.01 }}>
                  <label htmlFor="company" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                    Company (Optional)
                  </label>
                  <input
                    type="text"
                    name="company"
                    id="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.company
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-slate-gray/30'
                      }`}
                    placeholder="Your company name"
                  />
                </motion.div>

                <motion.div whileHover={{ scale: 1.01 }}>
                  <label htmlFor="reason" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                    Reason for Contact *
                  </label>
                  <select
                    name="reason"
                    id="reason"
                    required
                    value={formData.reason}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base ${validationErrors.reason
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-slate-gray/30'
                      }`}
                  >
                    <option value="">Select a reason</option>
                    {reasons.map(reason => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </motion.div>

                <motion.div whileHover={{ scale: 1.01 }}>
                  <label htmlFor="message" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full px-4 py-3 sm:py-4 border rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 resize-none bg-white/80 text-base ${validationErrors.message
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-slate-gray/30'
                      }`}
                    placeholder="Tell us more about your inquiry..."
                  />
                </motion.div>

                {/* Error Message */}
                {submissionError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-xl"
                    aria-live="polite"
                    role="alert"
                  >
                    <p className="text-red-600 text-sm font-medium">{submissionError}</p>
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-6 py-4 sm:py-5 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center text-base sm:text-lg min-h-[48px]"
                  aria-label="Send message"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;